import {
  OperationType,
  PricingMode,
  Unit,
} from '@rematter/frontend-lib-api-client';

import { TierInput } from '../types/pricing';

import { createMutationAtomFactory } from '~/shared/providers/api/createMutationAtomFactory';

export const customPriceListDraftAtom = createMutationAtomFactory(
  'brokerage.customPriceListDraft',
  (params: {
    facilityId: string;
    name: string;
    counterpartyGroupId: string;
    currency: string;
    operationType: OperationType;
    pricingMode?: PricingMode;
    startDate?: string | null;
    endDate?: string | null;
    notes?: string | null;
  }) => ({
    pricingCustomPriceListDraft: {
      __args: {
        input: {
          facilityId: params.facilityId,
          name: params.name,
          counterpartyGroupId: params.counterpartyGroupId,
          currency: params.currency,
          operationType: params.operationType,
          pricingMode: params.pricingMode,
          startDate: params.startDate,
          endDate: params.endDate,
          notes: params.notes,
        },
      },
      id: true,
      name: true,
      facilityId: true,
      counterpartyGroupId: true,
      currency: true,
      operationType: true,
      pricingMode: true,
      startDate: true,
      endDate: true,
      isPublished: true,
      notes: true,
      timestamps: {
        createdAt: true,
        updatedAt: true,
      },
    },
  }),
);

export const customPriceListUpdateAtom = createMutationAtomFactory(
  'brokerage.customPriceListUpdate',
  (params: {
    id: string;
    name?: string;
    counterpartyGroupId?: string;
    startDate?: string | null;
    endDate?: string | null;
    notes?: string | null;
  }) => ({
    pricingCustomPriceListUpdate: {
      __args: {
        id: params.id,
        input: {
          name: params.name,
          counterpartyGroupId: params.counterpartyGroupId,
          startDate: params.startDate,
          endDate: params.endDate,
          notes: params.notes,
        },
      },
      id: true,
      name: true,
      counterpartyGroupId: true,
      operationType: true,
      startDate: true,
      endDate: true,
      isPublished: true,
      archivedAt: true,
      notes: true,
      timestamps: {
        createdAt: true,
        updatedAt: true,
      },
    },
  }),
);

export const customPriceListDeleteAtom = createMutationAtomFactory(
  'brokerage.customPriceListDelete',
  (params: { id: string }) => ({
    pricingCustomPriceListDelete: {
      __args: {
        id: params.id,
      },
    },
  }),
);

export const customPriceListPublishAtom = createMutationAtomFactory(
  'brokerage.customPriceListPublish',
  (params: { id: string }) => ({
    pricingCustomPriceListPublish: {
      __args: {
        id: params.id,
      },
      id: true,
      isPublished: true,
      timestamps: {
        updatedAt: true,
      },
    },
  }),
);

export const customPriceListArchiveAtom = createMutationAtomFactory(
  'brokerage.customPriceListArchive',
  (params: { id: string }) => ({
    pricingCustomPriceListArchive: {
      __args: {
        id: params.id,
      },
    },
  }),
);

export const customPriceListUnarchiveAtom = createMutationAtomFactory(
  'brokerage.customPriceListUnarchive',
  (params: { id: string }) => ({
    pricingCustomPriceListUnarchive: {
      __args: {
        id: params.id,
      },
    },
  }),
);

export const customPriceListAddItemPricingAtom = createMutationAtomFactory(
  'brokerage.customPriceListAddItemPricing',
  (params: {
    customPriceListId: string;
    itemId: string;
    pricingUnit: Unit;
    position: number;
    tiers: TierInput[];
  }) => ({
    pricingCustomPriceListAddItemPricing: {
      __args: {
        customPriceListId: params.customPriceListId,
        input: {
          itemId: params.itemId,
          pricingUnit: params.pricingUnit,
          position: params.position,
          tiers: params.tiers,
        },
      },
      id: true,
      item: {
        id: true,
        name: true,
      },
      pricingUnit: true,
      position: true,
    },
  }),
);

export const customPriceListUpdateItemPricingAtom = createMutationAtomFactory(
  'brokerage.customPriceListUpdateItemPricing',
  (params: {
    itemPricingId: string;
    pricingUnit?: Unit;
    position?: number;
    tiers?: TierInput[];
  }) => ({
    pricingCustomPriceListUpdateItemPricing: {
      __args: {
        itemPricingId: params.itemPricingId,
        input: {
          pricingUnit: params.pricingUnit,
          position: params.position,
          tiers: params.tiers,
        },
      },
      id: true,
      pricingUnit: true,
      position: true,
    },
  }),
);

export const customPriceListDeleteItemPricingAtom = createMutationAtomFactory(
  'brokerage.customPriceListDeleteItemPricing',
  (params: { itemPricingId: string }) => ({
    pricingCustomPriceListDeleteItemPricing: {
      __args: {
        itemPricingId: params.itemPricingId,
      },
    },
  }),
);

export const customPriceListReorderItemPricingsAtom = createMutationAtomFactory(
  'brokerage.customPriceListReorderItemPricings',
  (params: { customPriceListId: string; itemPricingIds: string[] }) => ({
    pricingCustomPriceListReorderItemPricings: {
      __args: {
        customPriceListId: params.customPriceListId,
        itemPricingIds: params.itemPricingIds,
      },
    },
  }),
);

export const customPriceListBatchUpdateItemPricingsAtom =
  createMutationAtomFactory(
    'brokerage.customPriceListBatchUpdateItemPricings',
    (params: {
      customPriceListId: string;
      items: {
        itemPricingId: string;
        input: Record<string, unknown>;
        tiers?: TierInput[];
      }[];
    }) => ({
      pricingCustomPriceListBatchUpdateItemPricings: {
        __args: {
          customPriceListId: params.customPriceListId,
          items: params.items,
        },
        id: true,
        pricingUnit: true,
        position: true,
        tiersEffectiveAt: true,
      },
    }),
  );

export const customPriceListCopyFromAtom = createMutationAtomFactory(
  'brokerage.customPriceListCopyFrom',
  (params: {
    targetCustomPriceListId: string;
    sourceCustomPriceListId?: string;
    sourceDefaultPriceListId?: string;
  }) => ({
    pricingCustomPriceListCopyFrom: {
      __args: {
        targetCustomPriceListId: params.targetCustomPriceListId,
        sourceCustomPriceListId: params.sourceCustomPriceListId,
        sourceDefaultPriceListId: params.sourceDefaultPriceListId,
      },
      id: true,
      item: {
        id: true,
        name: true,
      },
      pricingUnit: true,
      position: true,
    },
  }),
);

export const customPriceListImportFromAtom = createMutationAtomFactory(
  'brokerage.customPriceListImportFrom',
  (params: { targetCustomPriceListId: string; itemPricingIds: string[] }) => ({
    pricingCustomPriceListImportFrom: {
      __args: {
        targetCustomPriceListId: params.targetCustomPriceListId,
        itemPricingIds: params.itemPricingIds,
      },
      id: true,
      item: {
        id: true,
        name: true,
      },
      pricingUnit: true,
      position: true,
    },
  }),
);

export const customPriceListDuplicateAtom = createMutationAtomFactory(
  'brokerage.customPriceListDuplicate',
  (params: { id: string; name: string }) => ({
    pricingCustomPriceListDuplicate: {
      __args: {
        id: params.id,
        name: params.name,
      },
      id: true,
      name: true,
      facilityId: true,
      counterpartyGroupId: true,
      currency: true,
      operationType: true,
      pricingMode: true,
      startDate: true,
      endDate: true,
      isPublished: true,
      notes: true,
      timestamps: {
        createdAt: true,
        updatedAt: true,
      },
    },
  }),
);

export const customPriceListAddProductPricingAtom =
  customPriceListAddItemPricingAtom;
export const customPriceListUpdateProductPricingAtom =
  customPriceListUpdateItemPricingAtom;
export const customPriceListDeleteProductPricingAtom =
  customPriceListDeleteItemPricingAtom;
export const customPriceListReorderProductPricingsAtom =
  customPriceListReorderItemPricingsAtom;
export const customPriceListBatchUpdateProductPricingsAtom =
  customPriceListBatchUpdateItemPricingsAtom;
